import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  type MenuItemProps,
} from '@mui/material';
import { type ExternalFile } from '@/lib/types';
import { useState } from 'react';
import Dialog from '@/components/Dialog';
import FilePicker from './FilePicker';
import useComplexState from '@/lib/utils/useComplexState';
import SelectedFilesContext from './SelectedFilesContext';
import PickerDialog from './PickerDialog';
import OneDriveIcon from '../utils/icons/OneDriveIcon';
import { MAX_EXTERNAL_FILES_SELECTED } from '../utils/constants';
import DialogConnectedAccountInfo from './DialogConnectedAccountInfo';
import oneDriveFilesCache from './FilePicker/oneDriveFilesCache';
import { FormattedMessage } from 'react-intl';
import IsMakingSelectedFilesPublicMessage from '../utils/IsMakingSelectedFilesPublicMessage';
import useMakeOneDriveExternalFilesPublic from './useMakeOneDriveExternalFilesPublic';
import { blockPageClose, unblockPageClose } from '@/lib/pageCloseBlocking';

type OneDriveFilePickerProps = Omit<MenuItemProps, 'onClick'> & {
  onPickFiles: (pickedFiles: ExternalFile[]) => void;
  onLoadStart: () => string;
  onLoadEnd: (loaderId: string) => void;
};

const OneDriveFilePicker = ({
  onPickFiles,
  onLoadStart,
  onLoadEnd,
  ...props
}: OneDriveFilePickerProps) => {
  const [pickerDialogOpen, setPickerDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useComplexState<
    Map<string, ExternalFile>
  >(new Map());

  const [isMakingSelectedFilesPublic, setIsMakingSelectedFilesPublic] =
    useState(false);
  const makeOneDriveExternalFilesPublic = useMakeOneDriveExternalFilesPublic();

  const selectFile = (externalFile: ExternalFile): void => {
    if (selectedFiles.size === MAX_EXTERNAL_FILES_SELECTED) {
      return;
    }

    setSelectedFiles(selectedFiles => {
      selectedFiles.set(externalFile.url, externalFile);
    });
  };

  const deselectFile = (externalFileUrl: string): void => {
    setSelectedFiles(selectedFiles => {
      selectedFiles.delete(externalFileUrl);
    });
  };

  const handlePick = async (): Promise<void> => {
    setPickerDialogOpen(false);

    const pageCloseBlockerId = blockPageClose();
    const loaderId = onLoadStart();

    setIsMakingSelectedFilesPublic(true);

    const externalFiles = Array.from(selectedFiles.values());

    const publicExternalFiles =
      await makeOneDriveExternalFilesPublic(externalFiles);

    onPickFiles(publicExternalFiles);

    setIsMakingSelectedFilesPublic(false);
    setSelectedFiles(new Map());

    onLoadEnd(loaderId);
    unblockPageClose(pageCloseBlockerId);
  };

  const closePicker = (): void => {
    setPickerDialogOpen(false);

    setSelectedFiles(new Map());
    oneDriveFilesCache.reset();
  };

  return (
    <>
      <MenuItem
        onClick={() => setPickerDialogOpen(true)}
        disabled={isMakingSelectedFilesPublic}
        {...props}
      >
        {isMakingSelectedFilesPublic ? (
          <IsMakingSelectedFilesPublicMessage />
        ) : (
          <>
            <ListItemIcon>
              <OneDriveIcon />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage id="portfolio.challenge.files.add.menu.items.select_from_onedrive" />
            </ListItemText>
          </>
        )}
      </MenuItem>
      <PickerDialog open={pickerDialogOpen} onClose={closePicker}>
        <SelectedFilesContext.Provider
          value={{
            selectedFiles,
            selectFile,
            deselectFile,
            canSelectMoreFiles:
              selectedFiles.size < MAX_EXTERNAL_FILES_SELECTED,
          }}
        >
          <FilePicker />
        </SelectedFilesContext.Provider>
        <Dialog.Actions>
          <DialogConnectedAccountInfo />
          <Dialog.Actions.CancelButton onClick={closePicker} />
          <Dialog.Actions.OkButton
            onClick={handlePick}
            disabled={selectedFiles.size === 0}
          >
            {selectedFiles.size === 0 && (
              <FormattedMessage id="portfolio.challenge.files.add.select.onedrive.file_picker.attach_button.no_files" />
            )}
            {selectedFiles.size === 1 && (
              <FormattedMessage id="portfolio.challenge.files.add.select.onedrive.file_picker.attach_button.one_file" />
            )}
            {selectedFiles.size > 1 && (
              <FormattedMessage
                id="portfolio.challenge.files.add.select.onedrive.file_picker.attach_button.multiple_files"
                values={{
                  selectedFilesCount: selectedFiles.size,
                }}
              />
            )}
          </Dialog.Actions.OkButton>
        </Dialog.Actions>
      </PickerDialog>
    </>
  );
};

export default OneDriveFilePicker;
